import { queryOptions } from '@tanstack/react-query'

/**
 * Paginile de tichet deschise DIN LINKUL UNUI EMAIL, de un om care nu are cont:
 * respingerea unei planificari, redeschiderea unui tichet rezolvat, si parerea
 * clientului dupa rezolvare.
 *
 * NU trec prin `apiGet` / `apiPost`, si asta e chiar miezul lor. La 401 acelea
 * cer un token nou si apoi TRIMIT OMUL LA ECRANUL DE INTRARE - iar cel care a
 * apasat linkul din email n-are cont in portal: ar fi ajuns la o pagina de
 * autentificare in loc de tichetul lui, si n-ar fi aflat niciodata de ce.
 * Acelasi drum ca `easeedeskCustomerTicket.ts`.
 *
 * Capetele stau sub `/api/v2/public/`, prefixul scos de sub firewall in
 * `config/packages/security.yaml`. Ce le apara e jetonul din adresa, verificat
 * pe server exact ca in portal (`checkToken`).
 *
 * DOUA FELURI DE RASPUNS CARE NU E 200, si se deosebesc:
 *
 *  - 410: linkul nu deschide nimic - jeton gresit, vechi, sau tichet care nu mai
 *    e. Un singur mesaj pentru toate trei, fiindca deosebirea dintre ele ar
 *    spune unui strain daca a nimerit un jeton adevarat.
 *  - 409 / 422: linkul e bun, dar ceva s-a schimbat intre timp sau nu e in
 *    regula - "tichetul nu mai e rezolvat", "planificarea a fost deja mutata".
 *    Astea SE SPUN, fiindca il privesc pe cel care a apasat.
 */

/** Linkul nu deschide nimic. */
export class TicketLinkGone extends Error {
  constructor() {
    super('This link is no longer valid.')
    this.name = 'TicketLinkGone'
  }
}

/** Linkul e bun, dar cererea a fost refuzata - si se spune de ce. */
export class TicketLinkRefused extends Error {
  readonly reason: string

  constructor(reason: string, message: string) {
    super(message)
    this.name = 'TicketLinkRefused'
    this.reason = reason
  }
}

const PUBLIC = '/api/v2/public/easeedesk'

async function call<T>(method: 'GET' | 'POST', path: string, body?: Record<string, unknown>): Promise<T> {
  const init: RequestInit = { method, headers: { Accept: 'application/json' } }

  if (body !== undefined) {
    const form = new URLSearchParams()
    for (const [name, value] of Object.entries(body)) {
      if (value === undefined || value === null) continue
      if (typeof value === 'object') {
        /* `attribute_id[1]=1` - forma pe care o citeste `$request->request->all()`. */
        for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
          form.append(`${name}[${key}]`, String(inner))
        }
        continue
      }
      form.append(name, String(value))
    }
    init.body = form
    init.headers = { ...init.headers, 'Content-Type': 'application/x-www-form-urlencoded' }
  }

  const response = await fetch(`${PUBLIC}${path}`, init)

  if (response.status === 410) throw new TicketLinkGone()

  if (!response.ok) {
    const refused = (await response.json().catch(() => null)) as { error?: string; message?: string } | null
    throw new TicketLinkRefused(refused?.error ?? 'error', refused?.message ?? 'This link could not be used.')
  }

  return (await response.json()) as T
}

/* -------------------------------------------------------------------------
 * PLANIFICARE RESPINSA - `easeedesk/reject_planned_ticket/<jeton>`
 * ---------------------------------------------------------------------- */

export interface TicketLinkPageData {
  ticket: number
  subject: string
}

export function plannedTicketQuery(token: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'link', 'planning', token],
    queryFn: () => call<TicketLinkPageData>('GET', `/planning/${encodeURIComponent(token)}`),
    retry: false,
  })
}

export function rejectPlanning(token: string, comments: string) {
  return call<{ status: string; ticket: number }>('POST', `/planning/${encodeURIComponent(token)}/reject`, {
    comments,
  })
}

/* -------------------------------------------------------------------------
 * REDESCHIDERE - `easeedesk/reopen_resolved_ticket/<jeton>`
 * ---------------------------------------------------------------------- */

export function resolvedTicketQuery(token: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'link', 'reopen', token],
    queryFn: () => call<TicketLinkPageData>('GET', `/reopen/${encodeURIComponent(token)}`),
    retry: false,
  })
}

export function reopenTicket(token: string, comments: string) {
  return call<{ status: string; ticket: number; ticketStatus: number }>(
    'POST',
    `/reopen/${encodeURIComponent(token)}`,
    { comments },
  )
}

/* -------------------------------------------------------------------------
 * PAREREA CLIENTULUI - `easeedesk/ticket_feedback/<jeton>/<nota>`
 * ---------------------------------------------------------------------- */

export interface FeedbackAssignee {
  user: number
  serviceProvider: number
  picture: string
  name: string
}

export interface FeedbackAttribute {
  id: number
  name: string
}

export interface FeedbackPageData {
  ticket: number
  rating: number
  /** Nota impartita la doi - macheta veche deseneaza stele pe jumatati. */
  stars: number
  assignee: FeedbackAssignee
  attributes: FeedbackAttribute[]
}

export function feedbackQuery(token: string, rating: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'link', 'feedback', token, rating],
    queryFn: () => call<FeedbackPageData>('GET', `/feedback/${encodeURIComponent(token)}/${rating}`),
    retry: false,
  })
}

export function sendFeedback(token: string, rating: number) {
  return call<{ status: string; ticket: number; feedback: number }>(
    'POST',
    `/feedback/${encodeURIComponent(token)}/${rating}`,
    {},
  )
}

/* -------------------------------------------------------------------------
 * APROBARE - `easeedesk/ticket/approve/<jeton>` si `.../deny/<jeton>`
 * ---------------------------------------------------------------------- */

/**
 * Ce ajuta omul sa hotarasca - numai nume si text, fara emailuri si fara nimic
 * intern (vezi `ApprovalLinks::details`). `summary` e TEXT, deja curatat de
 * etichete pe server, si se pune pe ecran ca text, niciodata ca HTML.
 * Campurile pot veni goale: pagina le sare pe cele fara valoare.
 */
export interface ApprovalDetails {
  type: string
  company: string
  requester: string
  /** `Y-m-d H:i:s`, ora serverului. */
  created: string
  priority: { name: string; color: string }
  summary: string
}

export interface ApprovalPageData {
  ticket: number
  subject: string
  action: 'approve' | 'deny'
  /** Numele furnizorului care cere hotararea; gol cand nu se stie. */
  provider?: string
  details?: ApprovalDetails
}

export function approvalQuery(action: 'approve' | 'deny', token: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'link', 'approval', action, token],
    queryFn: () => call<ApprovalPageData>('GET', `/approval/${action}/${encodeURIComponent(token)}`),
    retry: false,
  })
}

/**
 * Hotararea pleaca pe POST, niciodata pe GET.
 *
 * Prima varianta din portal aproba la simpla deschidere a adresei, si asa au
 * intrat tichete in "approved" in clipa in care a ajuns mailul: le apasase
 * previzualizarea clientului de mail, nu omul.
 */
export function applyApproval(action: 'approve' | 'deny', token: string, reason: string) {
  return call<{ status: string; ticket: number; message: string }>(
    'POST',
    `/approval/${action}/${encodeURIComponent(token)}`,
    { reason },
  )
}

export function completeFeedback(
  token: string,
  rating: number,
  post: { comments?: string; score_rating?: number; attribute_id?: Record<number, number> },
) {
  return call<{ status: string; ticket: number }>(
    'POST',
    `/feedback/${encodeURIComponent(token)}/${rating}/complete`,
    post,
  )
}
